import { CatalogFlow, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice } from './graph';
import {
  CatalogWizardArgs,
  asGroup,
  ensureBus,
  ensureController,
  ensureDashboard,
  ensureDevice,
  ensurePoint,
  ensureTimer,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

function pinOf(device: GraphDevice, key: 'sda_pin' | 'scl_pin', fallback: number): number {
  const n = Number(device[key]);
  return Number.isFinite(n) ? n : fallback;
}

/** Dual-write I2C (+ MQTT) groups and the SHT21 instance. Pins stay in settings. */
export function applySht21Hardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const sda = pinOf(device, 'sda_pin', 6);
  const scl = pinOf(device, 'scl_pin', 7);
  const connection = String(device.connection || 'i2c').trim() || 'i2c';
  const instance: GraphDevice = {
    ...device,
    id: String(device.id || 'sht21').trim() || 'sht21',
    type: 'sht21',
    enable: device.enable !== false,
    connection,
    sda_pin: sda,
    scl_pin: scl,
  };
  return {
    ...values,
    i2c: { ...asGroup(values.i2c), enable: true, sda_pin: sda, scl_pin: scl },
    sensor: { ...asGroup(values.sensor), enable: true, sda_pin: sda, scl_pin: scl },
    mqtt: { ...asGroup(values.mqtt), enable: true },
    devices: upsertDevice(values, instance),
  };
}

export function mergeSht21Catalog(doc: CatalogFlow, args: CatalogWizardArgs): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'sht21').trim() || 'sht21';
  const connection = String(args.device.connection || 'i2c').trim() || 'i2c';
  const interval = Number(args.interval) > 0 ? Math.round(Number(args.interval)) : 60;
  const controller = ensureController(state, args);
  const bus = ensureBus(state, {
    of: connection,
    busType: 'i2c',
    x: state.x0 + 160,
    y: state.y0,
  });
  const device = ensureDevice(state, {
    of,
    deviceType: 'sht21',
    x: state.x0 + 320,
    y: state.y0,
  });
  const timer = ensureTimer(state, interval, state.x0, state.y0 + 220);
  link(state, `${controller.id}.host`, `${bus.id}.host`);
  link(state, `${bus.id}.read`, `${device.id}.read`);
  const temp = ensurePoint(state, {
    of, key: 'temp', poll: true, x: state.x0 + 500, y: state.y0, deviceId: device.id,
  });
  const rh = ensurePoint(state, {
    of, key: 'rh', poll: true, x: state.x0 + 500, y: state.y0 + 140, deviceId: device.id,
  });
  const scale = { op: '*', value: 0.1 };
  const mqttTemp = wireToMqtt(state, {
    point: temp, topic: `{base}/${of}/temp`, y: state.y0, xCalc: state.x0 + 680, xMqtt: state.x0 + 840, timer, scale,
  });
  const mqttRh = wireToMqtt(state, {
    point: rh, topic: `{base}/${of}/rh`, y: state.y0 + 140, xCalc: state.x0 + 680, xMqtt: state.x0 + 840, timer, scale,
  });
  if (args.widget === 'temperature') {
    const dash = ensureDashboard(state, {
      widgetType: 'temperature',
      title: args.hostname ? `${args.hostname} · ${of}` : of,
      x: state.x0 + 1020,
      y: state.y0 + 70,
    });
    link(state, `${mqttTemp.id}.subscribe`, `${dash.id}.in`);
    link(state, `${mqttRh.id}.subscribe`, `${dash.id}.inRh`);
  }
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
